import { IEvent } from "@Interfaces";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    createAction,
    FETCH_STATUS,
    IPaymentInfoForSingleOffering,
    isEmpty,
    roundUp,
    STORAGE_KEYS,
} from "@Utils";
import { setSessionStorageItem } from "repoV2/utils/common/storage/getterAndSetters";
import { Dispatch } from "redux";
import { getEventCurrency } from "@Modules/eventPage/utils/getEventCurrency";
import { IDiscountField } from "./DiscountField/IDiscountField";
import { IDiscount } from "./IDiscount";
import { ILoyaltyDiscountsSection } from "./LoyaltyDiscountsSection/ILoyaltyDiscountsSection";
import { ILoyaltyDiscountsPopup } from "./LoyaltyDiscountsPopup/ILoyaltyDiscountsPopup";
import { InstalmentsDetailsContextProps } from "../PaymentPlan/utils/InstalmentsDetailsContext";

export const getDiscountProps = ({
    eventId,
    event,
    eventData,
    dispatch,
    hideDiscount,
    callApplyDiscountApi,
    mainScrolledComponentRef,
    disablePayment,
    showLoyaltyDiscountsPopup,
    setShowLoyaltyDiscountsPopup,
    showLoyaltyDiscountSection,
    isMobile,
    computedPaymentValues,
    isDiscountAppliedOnInstallments,
    refetchInstallments,
}: {
    eventId: string;
    event: IEvent.IStore;
    eventData: IEvent.IEventData;
    dispatch: Dispatch<any>;
    hideDiscount: boolean;
    callApplyDiscountApi: (
        newDiscountCode: string,
        newDiscountCodeType: IEvent.IAppliedDiscountCodeType,
        newCustomerEmail?: string
    ) => void;
    mainScrolledComponentRef: React.RefObject<HTMLDivElement>;
    disablePayment: boolean;
    showLoyaltyDiscountsPopup: boolean;
    setShowLoyaltyDiscountsPopup: (param: boolean) => void;
    showLoyaltyDiscountSection: boolean;
    isMobile: boolean;
    computedPaymentValues: IPaymentInfoForSingleOffering.IReturn;
    isDiscountAppliedOnInstallments?: boolean;
    refetchInstallments: InstalmentsDetailsContextProps["refetchInstallments"];
}): IDiscount.IProps => {
    const { discountEntered, discountError, appliedDiscountCodeType } =
        event.discount;

    const { slots, numberOfCustomers } = event.paymentInfo;

    const setAppliedDiscountCodeDetails = (
        appliedDiscountCodeTypeParam: IEvent.IAppliedDiscountCodeType,
        customerEmailParam?: string
    ) => {
        setSessionStorageItem(
            STORAGE_KEYS.DISCOUNT_CODE_TYPE,
            appliedDiscountCodeTypeParam
        );

        dispatch(
            createAction(
                ACTION_TYPES.EVENT.UPDATE_APPLIED_DISCOUNT_CODE_DETAILS,
                {
                    appliedDiscountCodeType: appliedDiscountCodeTypeParam,
                    customerEmail: customerEmailParam,
                }
            )
        );
    };

    const handleDiscountChange: (
        discountCodeInputValue: string,
        changedDiscountCodeType: IEvent.IAppliedDiscountCodeType
    ) => void = (discountCodeInputValue, changedDiscountCodeType) => {
        if (hideDiscount) return;

        /**
         * if the user applies a `"loyalty"` discount and then edits the `"normal"` discount field.
         * The discount should be moved to `"not applied"` state
         *
         * This holds true for normal case as well
         */
        if (changedDiscountCodeType === "normal") {
            setAppliedDiscountCodeDetails("not applied");
        }

        if (isDiscountAppliedOnInstallments) refetchInstallments();

        dispatch(
            createAction(ACTION_TYPES.EVENT.UPDATE_DISCOUNT, {
                discount: discountCodeInputValue.trim().toUpperCase(),
            })
        );
    };

    const { eventCurrency, eventCurrencySymbol } = getEventCurrency({
        eventData,
    });

    const handleNormalDiscountSubmit = (
        e: React.FormEvent<HTMLFormElement>
    ): void => {
        e.preventDefault();
        const discountCode = discountEntered.trim().toUpperCase();
        if (hideDiscount || isEmpty(discountCode)) return;
        callApplyDiscountApi(discountCode, "normal");
    };

    const {
        discounts: { discountPrice },
        flags: {
            isSingleSlot, // Is it a single slot type
        },
        factors: {
            discountScalingFactor,
            billingMultiplier, // Number of instances of items being bought for this SKU
        },
        priceValues: { unitPrice },
    } = computedPaymentValues;

    const disabled = disablePayment && !isSingleSlot && isEmpty(slots);

    const onDiscountCodeInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => handleDiscountChange(e.target.value, "normal");

    const onDiscountFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        // To scroll up the discount code box when the keyboard is opened
        mainScrolledComponentRef?.current?.scrollTo({
            top: 0.8 * e?.target?.offsetTop,
            behavior: "smooth",
        });
    };

    const loyaltyDiscountsSectionProps: ILoyaltyDiscountsSection.IProps = {
        hideComponent: !(eventData?.show_loyalty && showLoyaltyDiscountSection),
        openLoyaltyDiscountsPopup: () => {
            setShowLoyaltyDiscountsPopup(true);
        },
    };

    const discountFieldProps: IDiscountField.IBaseProps = {
        hideComponent: disablePayment || hideDiscount,
        discountCodeInput: discountEntered,
        discountFieldDisabled: disabled,
        onDiscountCodeInputChange,
        onDiscountFieldFocus,
        applyButtonDisabled: !!(
            isEmpty(discountEntered) ||
            disabled ||
            discountError ||
            discountPrice
        ),
        discountApplied: discountPrice > 0 || !!isDiscountAppliedOnInstallments,
        discountError,
        handleNormalDiscountSubmit,
        loyaltyDiscountsSectionProps,
        isMobile,
    };

    const onLoyaltyDiscountCodeChange = (code: string) =>
        handleDiscountChange(code, "loyalty");

    const handleLoyaltyDiscountSubmit = (
        discountCode: string,
        customerEmail: string
    ): void => {
        if (hideDiscount || discountCode.length === 0) return;
        callApplyDiscountApi(discountCode, "loyalty", customerEmail);
    };

    const loyaltyDiscountCodeList: Array<IEvent.ILoyaltyDiscount> =
        event.loyaltyDiscounts.data;

    const resetLoyaltyDiscountList = () => {
        dispatch(createAction(ACTION_TYPES.EVENT.RESET_LOYALTY_DISCOUNT_LIST));
        dispatch(
            createAction(ACTION_TYPES.UTILS.UPDATE_API_CALL_STATUS, {
                [API_ACTION_TYPES.GET_LOYALTY_DISCOUNT_LIST]:
                    FETCH_STATUS.NOT_FETCHED,
            })
        );
    };

    const closePopup = () => {
        setShowLoyaltyDiscountsPopup(false);
        resetLoyaltyDiscountList();
    };

    /**
     * TODO: Refactor. Data should be calculated at a common place to maintain single source of truth.
     * logic from `src/components/eventPage/components/PaymentSummary/components/ExpandedBill.tsx`
     */
    const discountedPrice = roundUp(
        Math.min(
            unitPrice * billingMultiplier * numberOfCustomers,
            discountPrice * discountScalingFactor
        )
    );

    const loyaltyDiscountsPopupProps: ILoyaltyDiscountsPopup.IProps = {
        hideComponent: !showLoyaltyDiscountsPopup,
        closePopup,
        eventId,
        loyaltyDiscountCodeList,
        resetLoyaltyDiscountList,
        handleLoyaltyDiscountSubmit,
        onLoyaltyDiscountCodeChange,
        selectedLoyaltyDiscountCode: discountEntered,
        discountApplied: appliedDiscountCodeType === "loyalty",
        discountedPrice,
        showDiscountedPrice: discountedPrice > 0,
        eventCurrency,
        eventCurrencySymbol,
    };

    return {
        discountFieldProps,
        loyaltyDiscountsPopupProps,
    };
};
